import { definePermissions } from '@eis/core';

/**
 * Insurance permission catalog (Addendum A.2 "ownership").
 *
 * This file is the ONLY place in the module where permission literals may appear — ESLint
 * enforces that. Everything else references `insurancePermissions.groupPolicies.view`, so a typo is
 * a compile error rather than a permission that silently denies in production.
 *
 * Keys map 1:1 to the strings the auth service issues; `permissions-registry.json` is generated
 * from here and diffed against the backend's exported list.
 */
export const insurancePermissions = definePermissions({
  groupPolicies: {
    domain: 'policy-management',
    entity: 'group-policies',
    actions: ['view', 'create', 'update', 'terminate'],
  },
  /**
   * `update` is an ENDORSEMENT: it extends the timeline forward and moves the business version.
   *
   * `correct` is separate and deliberately so. A correction re-states history over business time
   * it already covered and raises an adjustment memo against money that has been invoiced
   * (service summary §4.2), so "who may re-state history" is a different question from "who may
   * change a policy going forward" — one the platform has open as O-4/P-4. Modelling it as one
   * action would answer that question by accident, in the direction that grants more.
   *
   * `terminate` and `detach` are endorsements too, at the storage layer. They are separate
   * actions because each ends something a customer is paying for.
   *
   * An UPGRADE to a newer tariff level (SDD-016 §5) is `update`: it is an endorsement that moves
   * the pin, and a separate action would widen every wildcard grant the day it existed.
   */
  policies: {
    domain: 'policy-management',
    entity: 'policies',
    actions: ['view', 'create', 'update', 'correct', 'terminate', 'detach', 'delete'],
  },
  // `publish` is separate from `update` for the same reason it is on group tariffs: publishing
  // runs the gate, freezes an immutable artifact and moves the generation's active version.
  // Whoever may draft a manifest is not automatically whoever may sign one off. Since SDD-016
  // (prompt 23): drafting a VERSION is `update`; publishing it, discarding it, and changing a
  // published version's status — activate AND retire, one grant — are `publish`.
  products: {
    domain: 'policy-management',
    entity: 'products',
    actions: ['view', 'create', 'update', 'publish'],
  },
  groupTariffs: {
    domain: 'policy-management',
    entity: 'group-tariffs',
    actions: ['view', 'create', 'update', 'publish'],
  },
  /**
   * A lead is a NEW ENTITY, not new actions on `policies` (SDD-013 §8.2, prompt 19): it has no
   * PolicyStatus, is not in force and is never billed, and giving whoever may bind a policy the
   * right to open a lead — or the reverse — is exactly the coupling D-L7 separates. `approve` is
   * the one guarded transition of the pinned lifecycle (`requires: approve`); `update` covers the
   * body, the unguarded transitions and the refresh. Accepting a lead mints a policy and is
   * `policies.create`, reused.
   */
  leads: {
    domain: 'policy-management',
    entity: 'leads',
    actions: ['view', 'create', 'update', 'approve'],
  },
  /**
   * An EXPORT is a new ENTITY (SDD-020 J-20, §10, prompt 28): a file of bulk personal data that
   * leaves the system. Viewing a list is not enough to take it away, so `create` here is the right
   * to start an export job; the service also checks the VIEW key of what is exported. Granted to
   * operations-manager and product-configurator (decided with the owner on 2026-10-04).
   */
  exports: {
    domain: 'policy-management',
    entity: 'exports',
    actions: ['create'],
  },
  /**
   * The PARTNER domain's key (SDD-013 D-L4 ii, SDD-011 P-12): the right to ask the Partner
   * service to create a partner from a lead's draft (`RegisterLeadPartners`, `register_partners`
   * on an import, the accept's own registration — SDD-014 PS-5). OWNED by `modules/partners`
   * since prompt 20 (SDD-014 §8, R-12); kept here as the typed handle this module's gates need,
   * because a module may not import another (backbone §1). The `reference:` marker below is read
   * by tools/scripts/export-permissions-registry.mjs, which lists the key once, under its owner.
   */
  partners: {
    // reference: owned by modules/partners/src/permissions.ts
    domain: 'partner-management',
    entity: 'partners',
    actions: ['view', 'create'],
  },
  /**
   * The BILLING domain's keys (SDD-015 §7.4), OWNED by `modules/billing` since prompt 21 phase 5
   * and referenced here for the same reason `partners` is: the policy page's `billing` tab and the
   * group policy's `invoices` tab READ billing through `@eis/core` hooks and gate that section
   * with `invoices.view`; their action buttons send the operator into the billing module's
   * dialogs (link builders, never an import) and show only for whoever may `create` or
   * `correct` there. A persona that may read a policy but not its money sees the policy and no
   * billing section — never a 403 on the policy.
   */
  invoices: {
    // reference: owned by modules/billing/src/permissions.ts
    domain: 'billing-management',
    entity: 'invoices',
    actions: ['view', 'create', 'correct'],
  },
  /**
   * The CLAIMS domain's keys (SDD-018 C-13), OWNED by `modules/claims` since prompt 26 phase 26.6
   * and referenced here like `invoices`: the policy page's and the group policy's "Schäden" tabs
   * READ claimservice through `@eis/core` hooks and are gated with `claims.view`; "Schaden melden"
   * sends the operator to the claims module's create route (`links.claims.newClaim`, never an
   * import) and shows only for whoever holds `claims.create`. A persona that may read a policy but
   * not its claims sees the policy and no tab — never a 403 on the policy.
   */
  claims: {
    // reference: owned by modules/claims/src/permissions.ts
    domain: 'claim-management',
    entity: 'claims',
    actions: ['view', 'create'],
  },
});
